import * as React from 'react';
import type {Selection} from '@react-types/shared';
import type {FormAdapter, FormFieldValue} from '../useFormRegister';

const isReactEvent = (
  event: unknown,
): event is React.ChangeEvent<HTMLElement> => {
  return !!event && typeof event === 'object' && 'target' in event;
};

type SearchFieldValues = FormFieldValue | Selection;

type ChangeAdapter = FormAdapter<
  FormFieldValue,
  SearchFieldValues
>['changeAdapter'];

type SearchFieldFormAdapter = {
  valueAdapter: (
    fieldValue: FormFieldValue | SearchFieldValues,
    controlValue?: FormFieldValue | SearchFieldValues,
  ) => SearchFieldValues;
  changeAdapter: (
    eventOrValue: React.ChangeEvent<HTMLElement> | SearchFieldValues,
    fieldValue: FormFieldValue,
    controlValue?: FormFieldValue,
  ) => FormFieldValue;
};

export function useSearchFieldFormAdapter(
  isMultiSelect: boolean,
): SearchFieldFormAdapter {
  const multiSelectValueAdapter: FormAdapter<SearchFieldValues>['valueAdapter'] =
    React.useCallback((fieldValue) => {
      if (fieldValue instanceof Set) {
        return fieldValue;
      }
      if (Array.isArray(fieldValue)) {
        return new Set(fieldValue);
      }

      return new Set(fieldValue ? [String(fieldValue)] : []);
    }, []);

  const multiSelectChangeAdapter: ChangeAdapter = React.useCallback(
    (eventOrValue, fieldValue) => {
      // React events first - ignore and return current field value
      if (isReactEvent(eventOrValue)) {
        return Array.isArray(fieldValue) ? fieldValue : [];
      }

      // Set/Selection objects - convert to string array
      if (eventOrValue instanceof Set) {
        return Array.from(eventOrValue).map(String);
      }

      // arrays - pass through unchanged
      if (Array.isArray(eventOrValue)) {
        return eventOrValue;
      }

      // other types - return current field value or empty array
      return Array.isArray(fieldValue) ? fieldValue : [];
    },
    [],
  );

  const singleSelectChangeAdapter: ChangeAdapter = React.useCallback(
    (eventOrValue, fieldValue) => {
      // React events - ignore and return current field value
      if (isReactEvent(eventOrValue)) {
        return fieldValue;
      }

      // Set/Selection objects
      if (eventOrValue instanceof Set) {
        if (eventOrValue.size === 0) {
          return fieldValue; // Keep current value if Set is empty
        }
        return Array.from(eventOrValue)[0]; // Extract first key
      }

      // other types - pass through unchanged
      return eventOrValue;
    },
    [],
  );

  return React.useMemo(
    () => ({
      valueAdapter: isMultiSelect
        ? multiSelectValueAdapter
        : (eventOrValue) => eventOrValue,
      changeAdapter: isMultiSelect
        ? multiSelectChangeAdapter
        : singleSelectChangeAdapter,
    }),
    [
      isMultiSelect,
      multiSelectValueAdapter,
      multiSelectChangeAdapter,
      singleSelectChangeAdapter,
    ],
  );
}
